<template>
  <v-navigation-drawer
    :value="value"
    app
    right
    temporary
    width="480"
    class="cart-drawer"
    :class="{ mobile: $vuetify.breakpoint.xsOnly }"
    @input="$emit('input', $event)">
    <header class="cart-header">
      <div>
        <div class="cart-eyebrow">Bestellung vorbereiten</div>
        <div class="d-flex align-center">
          <v-icon left color="white">mdi-cart-outline</v-icon>
          <span class="title-text">Warenkorb</span>
          <v-chip v-if="totalPositions" small color="white" text-color="primary" class="ml-2">{{ totalPositions }}</v-chip>
        </div>
      </div>
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn v-bind="attrs" v-on="on" icon color="white" aria-label="Warenkorb schließen" @click="$emit('input', false)">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </template>
        <span>Schließen</span>
      </v-tooltip>
    </header>

    <div v-if="items.length === 0" class="empty-cart">
      <div class="empty-cart__icon"><v-icon size="44" color="primary">mdi-cart-outline</v-icon></div>
      <h2>Dein Warenkorb ist leer</h2>
      <p>Entdecke Werbemittel und Downloads für dein Studio.</p>
      <v-btn color="primary" depressed rounded class="text-none" @click="$emit('input', false)">
        <v-icon left>mdi-storefront-outline</v-icon>
        Produkte ansehen
      </v-btn>
    </div>

    <div v-else class="cart-body">
      <div class="cart-scroll">
        <section class="cart-items" aria-labelledby="cart-items-title">
          <div class="cart-section-heading">
            <div>
              <h2 id="cart-items-title">Deine Auswahl</h2>
              <span>{{ totalPositions }} {{ totalPositions === 1 ? "Position" : "Positionen" }}</span>
            </div>
            <span>{{ totalItems }} {{ totalItems === 1 ? "Stück" : "Stück" }}</span>
          </div>

          <transition-group name="cart-list" tag="div">
            <article
              v-for="item in items"
              :key="getCartItemKey(item)"
              class="cart-item"
              :class="{ 'is-digital': item.productType === 'digital', 'has-error': isItemInvalid(item) }">
              <div class="item-main">
                <div class="item-thumb" aria-hidden="true">
                  <cart-product-thumb v-if="getProduct(item)" :product="getProduct(item)" :selected-options="item.selectedOptions" />
                  <v-icon v-else color="grey darken-1">mdi-image-outline</v-icon>
                </div>

                <div class="item-info">
                  <div class="item-kicker">
                    <v-icon small :color="item.productType === 'digital' ? 'primary' : 'blue-grey lighten-2'">
                      {{ item.productType === "digital" ? "mdi-download-outline" : "mdi-package-variant-closed" }}
                    </v-icon>
                    {{ item.productType === "digital" ? "Download" : "Physisches Produkt" }}
                  </div>
                  <h3 class="item-title">{{ item.title }}</h3>
                  <div v-if="hasOptions(item)" class="item-options">
                    <v-chip v-for="(val, name) in item.selectedOptions" :key="name" x-small outlined color="grey lighten-2">
                      {{ name }}: {{ val }}
                    </v-chip>
                  </div>
                  <div v-if="getStockNotice(item)" class="stock-notice" :class="{ error: isItemInvalid(item) }">
                    <v-icon x-small :color="isItemInvalid(item) ? 'error' : 'warning'">mdi-alert-circle-outline</v-icon>
                    {{ getStockNotice(item) }}
                  </div>
                </div>
              </div>

              <div class="item-actions">
                <div v-if="item.productType !== 'digital'" class="qty-stepper" aria-label="Menge ändern">
                  <v-btn color="white" icon aria-label="Menge reduzieren" :disabled="item.quantity <= item.minQuantity" @click="changeQty(item, -1)">
                    <v-icon small>mdi-minus</v-icon>
                  </v-btn>
                  <input
                    :value="item.quantity"
                    type="number"
                    inputmode="numeric"
                    class="qty-input"
                    aria-label="Menge"
                    :min="item.minQuantity"
                    :max="getMaxQty(item)"
                    @input="setQty(item, $event.target.value)"
                    @blur="clampQty(item)" />
                  <v-btn color="white" icon aria-label="Menge erhöhen" :disabled="item.quantity >= getMaxQty(item)" @click="changeQty(item, 1)">
                    <v-icon small>mdi-plus</v-icon>
                  </v-btn>
                </div>
                <div v-else class="digital-quantity"><v-icon small>mdi-check-circle-outline</v-icon> 1 Download</div>

                <v-tooltip left>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      v-bind="attrs"
                      v-on="on"
                      icon
                      color="error"
                      :aria-label="`${item.title} entfernen`"
                      @click="$emit('remove-item', getCartItemKey(item))">
                      <v-icon>mdi-trash-can-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Entfernen</span>
                </v-tooltip>
              </div>
            </article>
          </transition-group>
        </section>

        <section class="cart-form" aria-labelledby="delivery-title">
          <div class="cart-section-heading form-heading">
            <div>
              <h2 id="delivery-title">Lieferung &amp; Angaben</h2>
              <span>Das Studio ist das interne Liefer- und Kostenziel.</span>
            </div>
          </div>

          <v-select
            v-model="selectedStudio"
            :items="studioOptions"
            item-text="name"
            item-value="_id"
            label="Studio auswählen *"
            prepend-inner-icon="mdi-map-marker-outline"
            outlined
            dense
            dark
            :error="!selectedStudio"
            :hint="selectedStudio ? `Lieferziel: ${selectedStudioName}` : 'Bitte wähle ein Studio aus.'"
            persistent-hint
            class="mb-4" />

          <v-textarea
            v-model="note"
            label="Notiz an das Marketing-Team (optional)"
            placeholder="z. B. gewünschter Einsatztermin oder ergänzende Hinweise"
            outlined
            dense
            dark
            rows="3"
            :counter="500"
            maxlength="500"
            auto-grow />
        </section>
      </div>

      <footer class="cart-footer">
        <div class="cart-summary">
          <span>Zusammenfassung</span>
          <strong>{{ totalPositions }} {{ totalPositions === 1 ? "Position" : "Positionen" }} · {{ totalItems }} Stück</strong>
        </div>

        <div v-if="hasDigitalItems" class="checkout-note success-note">
          <v-icon small color="success">mdi-download-circle-outline</v-icon>
          Downloads erscheinen nach erfolgreicher Bestellung unter „Meine Bestellungen“.
        </div>

        <v-alert v-if="submitBlocker" dense text color="warning" icon="mdi-information-outline" class="submit-blocker">
          {{ submitBlocker }}
        </v-alert>

        <v-btn
          block
          large
          color="primary"
          depressed
          rounded
          class="text-none submit-btn"
          :loading="submitting"
          :disabled="!canSubmit"
          @click="submit">
          Bestellung verbindlich absenden
          <v-icon right>mdi-arrow-right</v-icon>
        </v-btn>

        <v-expand-transition>
          <div v-if="clearConfirm" class="clear-confirm" role="alert">
            <span>Alle Positionen entfernen?</span>
            <div>
              <v-btn text small class="text-none" @click="clearConfirm = false">Abbrechen</v-btn>
              <v-btn text small color="error" class="text-none" @click="clearCartConfirmed">Leeren</v-btn>
            </div>
          </div>
        </v-expand-transition>
        <v-btn v-if="!clearConfirm" block text small color="grey lighten-1" class="text-none clear-btn" :disabled="submitting" @click="confirmClear">
          Warenkorb leeren
        </v-btn>
      </footer>
    </div>
  </v-navigation-drawer>
</template>

<script>
import api from "@/api"

const CartProductThumb = {
  name: "CartProductThumb",
  props: {
    product: { type: Object, required: true },
    selectedOptions: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      url: null,
      loading: false,
      failed: false,
      requestId: 0,
    }
  },
  computed: {
    imageSignature() {
      const options = Object.entries(this.selectedOptions || {})
        .sort()
        .map(([name, value]) => `${name}:${value}`)
        .join("|")
      return `${this.product && this.product._id ? this.product._id : ""}-${options}`
    },
  },
  watch: {
    imageSignature: {
      immediate: true,
      handler() {
        this.loadImage()
      },
    },
  },
  beforeDestroy() {
    this.requestId += 1
    this.releaseUrl()
  },
  methods: {
    releaseUrl() {
      if (!this.url) return
      URL.revokeObjectURL(this.url)
      this.url = null
    },
    getTargetFileId() {
      const options = this.selectedOptions || {}
      for (const option of this.product.options || []) {
        const choice = (option.choices || []).find((entry) => entry.value === options[option.name])
        if (choice && choice.imageFileId) return choice.imageFileId
      }
      return this.product.images && this.product.images.length ? this.product.images[0].fileId : null
    },
    async loadImage() {
      const currentRequest = ++this.requestId
      this.releaseUrl()
      this.failed = false
      this.loading = false
      const fileId = this.getTargetFileId()
      if (!fileId) return

      this.loading = true
      try {
        const url = await api.marketingShop.loadProductImage(this.product._id, fileId)
        if (currentRequest !== this.requestId) {
          URL.revokeObjectURL(url)
          return
        }
        this.url = url
      } catch (error) {
        if (currentRequest === this.requestId) this.failed = true
      } finally {
        if (currentRequest === this.requestId) this.loading = false
      }
    },
    handleImageError() {
      this.failed = true
      this.releaseUrl()
    },
  },
  render(h) {
    let content

    if (this.loading) {
      content = h("v-progress-circular", {
        props: { indeterminate: true, size: 20, width: 2, color: "primary" },
      })
    } else if (this.url && !this.failed) {
      content = h("img", {
        attrs: {
          src: this.url,
          alt: this.product.title || "",
        },
        on: { error: this.handleImageError },
      })
    } else {
      content = h("v-icon", { props: { color: "grey darken-1" } }, ["mdi-image-outline"])
    }

    return h("div", { class: "cart-thumb-content" }, [content])
  },
}

export default {
  name: "CartDrawer",
  components: { CartProductThumb },
  props: {
    value: { type: Boolean, default: false },
    items: { type: Array, default: () => [] },
    products: { type: Array, default: () => [] },
    studioOptions: { type: Array, default: () => [] },
    defaultStudioId: { type: String, default: null },
    submitting: { type: Boolean, default: false },
  },
  data() {
    return {
      selectedStudio: this.defaultStudioId,
      note: "",
      clearConfirm: false,
    }
  },
  computed: {
    totalPositions() {
      return this.items.length
    },
    totalItems() {
      return this.items.reduce((sum, i) => sum + (parseInt(i.quantity) || 0), 0)
    },
    selectedStudioName() {
      const studio = this.studioOptions.find((entry) => String(entry._id) === String(this.selectedStudio))
      return studio ? studio.name : ""
    },
    invalidItems() {
      return this.items.filter((item) => this.isItemInvalid(item))
    },
    submitBlocker() {
      if (!this.selectedStudio) return "Wähle ein Studio als Liefer- und Kostenziel aus."
      if (this.invalidItems.length) {
        return this.invalidItems.length === 1
          ? "Eine Position ist in dieser Menge nicht verfügbar. Passe sie an oder entferne sie."
          : `${this.invalidItems.length} Positionen sind in dieser Menge nicht verfügbar.`
      }
      return ""
    },
    canSubmit() {
      return this.items.length > 0 && !this.submitBlocker && !this.submitting
    },
    hasDigitalItems() {
      return this.items.some((i) => i.productType === "digital")
    },
  },
  watch: {
    value(open) {
      if (open && !this.selectedStudio) this.selectedStudio = this.defaultStudioId
      if (!open) this.clearConfirm = false
    },
    defaultStudioId(val) {
      if (!this.selectedStudio) this.selectedStudio = val
    },
    totalPositions(current, previous) {
      if (previous > 0 && current === 0) {
        this.note = ""
        this.clearConfirm = false
      }
    },
  },
  methods: {
    getCartItemKey(item) {
      if (!item.selectedOptions || Object.keys(item.selectedOptions).length === 0) return item.product
      const optStr = Object.entries(item.selectedOptions)
        .sort()
        .map(([k, v]) => `${k}:${v}`)
        .join(",")
      return `${item.product}_${optStr}`
    },
    hasOptions(item) {
      return item.selectedOptions && Object.keys(item.selectedOptions).length > 0
    },
    getProduct(item) {
      return this.products.find((product) => String(product._id) === String(item.product)) || null
    },
    getCartItemVariantSignature(item) {
      const prod = this.products.find((p) => String(p._id) === String(item.product))
      if (!prod || !prod.options || prod.options.length === 0) return ""

      const selectedChoiceIds = []
      const opts = item.selectedOptions || {}

      for (const opt of prod.options) {
        const selectedVal = opts[opt.name]
        if (!selectedVal) continue
        const choice = (opt.choices || []).find((c) => c.value === selectedVal)
        if (choice && choice.id) {
          selectedChoiceIds.push(choice.id)
        }
      }
      return selectedChoiceIds.sort().join(",")
    },
    getProductStock(item) {
      const prod = this.getProduct(item)
      if (!prod) return null

      if (prod.options && prod.options.length > 0) {
        const sig = this.getCartItemVariantSignature(item)
        if (!prod.variantStock) return null
        const val = prod.variantStock[sig]
        return val !== undefined ? val : null
      }
      return prod.stock !== undefined && prod.stock !== null ? prod.stock : null
    },
    isItemInvalid(item) {
      const stock = this.getProductStock(item)
      return stock !== undefined && stock !== null && (item.quantity > stock || stock <= 0)
    },
    getStockNotice(item) {
      const stock = this.getProductStock(item)
      if (stock === undefined || stock === null || item.productType === "digital") return ""
      if (stock <= 0) return "Ausverkauft – bitte entfernen."
      if (item.quantity > stock) return `Nur noch ${stock} Stück verfügbar.`
      const lowStockThreshold = Math.max(10, (item.minQuantity || 1) * 2)
      if (stock <= lowStockThreshold) return `Noch ${stock} Stück verfügbar.`
      return ""
    },
    getMaxQty(item) {
      const stock = this.getProductStock(item)
      if (stock !== undefined && stock !== null) {
        return Math.min(item.maxQuantity || 100, stock)
      }
      return item.maxQuantity || 100
    },
    changeQty(item, delta) {
      if (item.productType === "digital") return
      const max = this.getMaxQty(item)
      const newQty = Math.max(item.minQuantity || 1, Math.min(max, (item.quantity || 0) + delta))
      this.$emit("update-quantity", { itemKey: this.getCartItemKey(item), quantity: newQty })
    },
    setQty(item, val) {
      if (item.productType === "digital") return
      const max = this.getMaxQty(item)
      const v = Math.max(item.minQuantity || 1, Math.min(max, parseInt(val) || item.minQuantity || 1))
      this.$emit("update-quantity", { itemKey: this.getCartItemKey(item), quantity: v })
    },
    clampQty(item) {
      if (item.productType === "digital") return
      const max = this.getMaxQty(item)
      const v = Math.max(item.minQuantity || 1, Math.min(max, item.quantity || item.minQuantity || 1))
      this.$emit("update-quantity", { itemKey: this.getCartItemKey(item), quantity: v })
    },
    submit() {
      if (!this.canSubmit) return
      this.$emit("submit-order", { studio: this.selectedStudio, note: this.note })
    },
    confirmClear() {
      this.clearConfirm = true
    },
    clearCartConfirmed() {
      this.$emit("clear-cart")
      this.clearConfirm = false
    },
  },
}
</script>

<style scoped>
.cart-drawer {
  --cart-border: rgba(255, 255, 255, 0.09);
  --cart-surface: rgba(255, 255, 255, 0.045);
  --cart-muted: rgba(255, 255, 255, 0.6);
  background: #191c1a !important;
  color: #fff !important;
}

.cart-drawer ::v-deep .v-navigation-drawer__content {
  display: flex;
  min-height: 100%;
  flex-direction: column;
}

.cart-drawer.mobile {
  width: 100vw !important;
}

.cart-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 92px;
  padding: 18px 22px;
  background: radial-gradient(circle at 88% 0%, rgba(255, 255, 255, 0.18), transparent 36%), linear-gradient(135deg, #06a64b 0%, #00672d 100%);
  color: #fff;
  flex-shrink: 0;
}

.cart-eyebrow {
  margin-bottom: 3px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.13em;
  text-transform: uppercase;
}

.title-text {
  font-size: 1.22rem;
  font-weight: 700;
  margin-left: 8px;
}

.empty-cart {
  display: flex;
  min-height: calc(100vh - 92px);
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 28px;
  text-align: center;
}

.empty-cart__icon {
  display: grid;
  width: 88px;
  height: 88px;
  margin-bottom: 22px;
  place-items: center;
  border: 1px solid rgba(0, 168, 79, 0.2);
  border-radius: 28px;
  background: rgba(0, 168, 79, 0.1);
}

.empty-cart h2 {
  margin: 0 0 8px;
  font-size: 1.2rem;
}

.empty-cart p {
  max-width: 300px;
  margin: 0 0 24px;
  color: var(--cart-muted);
  line-height: 1.5;
}

.cart-body {
  display: flex;
  min-height: 0;
  flex-direction: column;
  flex-grow: 1;
  overflow: hidden;
}

.cart-scroll {
  min-height: 0;
  flex: 1 1 auto;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.cart-items {
  padding: 22px 18px 8px;
}

.cart-section-heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
  color: var(--cart-muted);
  font-size: 0.78rem;
}

.cart-section-heading h2 {
  margin: 0 0 2px;
  color: #fff;
  font-size: 1rem;
  letter-spacing: -0.01em;
}

.cart-section-heading > span {
  font-weight: 600;
}

.cart-item {
  display: flex;
  padding: 12px;
  margin-bottom: 12px;
  flex-direction: column;
  border: 1px solid var(--cart-border);
  border-radius: 14px;
  background: var(--cart-surface);
  transition: border-color 160ms ease, background-color 160ms ease, transform 160ms ease;
}

.cart-item.is-digital {
  border-color: rgba(0, 168, 79, 0.28);
  background: rgba(0, 168, 79, 0.055);
}

.cart-item.has-error {
  border-color: rgba(244, 67, 54, 0.58);
  background: rgba(244, 67, 54, 0.06);
}

.item-main {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.item-thumb {
  display: grid;
  overflow: hidden;
  width: 68px;
  height: 68px;
  flex: 0 0 68px;
  place-items: center;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 11px;
  background: rgba(0, 0, 0, 0.2);
}

.item-thumb ::v-deep .cart-thumb-content,
.item-thumb ::v-deep .cart-thumb-content img {
  width: 100%;
  height: 100%;
}

.item-thumb ::v-deep .cart-thumb-content {
  display: grid;
  place-items: center;
}

.item-thumb ::v-deep img {
  object-fit: cover;
}

.item-info {
  flex-grow: 1;
  min-width: 0;
}

.item-kicker {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 3px;
  color: rgba(255, 255, 255, 0.48);
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.item-title {
  display: -webkit-box;
  overflow: hidden;
  margin: 0;
  font-size: 0.9rem;
  font-weight: 650;
  line-height: 1.35;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.item-options {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 7px;
}

.stock-notice {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 7px;
  color: #ffc66d;
  font-size: 0.7rem;
  font-weight: 600;
}

.stock-notice.error {
  color: #ff8a80;
}

.item-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.065);
}

.qty-stepper {
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 999px;
  padding: 2px;
}

.qty-stepper ::v-deep .v-btn,
.item-actions > ::v-deep .v-btn {
  width: 40px;
  height: 40px;
}

.qty-input {
  width: 48px;
  text-align: center;
  background: transparent;
  border: none;
  color: #fff;
  font-weight: 700;
  font-size: 0.84rem;
  outline: none;
  -moz-appearance: textfield;
}

.qty-input::-webkit-outer-spin-button,
.qty-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.digital-quantity {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #78dc9e;
  font-size: 0.75rem;
  font-weight: 600;
}

.cart-form {
  margin-top: 12px;
  padding: 22px 18px;
  border-top: 1px solid var(--cart-border);
  background: rgba(255, 255, 255, 0.018);
}

.form-heading {
  align-items: flex-start;
}

.cart-footer {
  z-index: 2;
  padding: 16px 18px 14px;
  border-top: 1px solid var(--cart-border);
  background: rgba(25, 28, 26, 0.97);
  box-shadow: 0 -16px 36px rgba(0, 0, 0, 0.2);
  flex-shrink: 0;
}

.cart-summary {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
  color: var(--cart-muted);
  font-size: 0.78rem;
}

.cart-summary strong {
  color: rgba(255, 255, 255, 0.9);
  text-align: right;
}

.checkout-note {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  margin-bottom: 10px;
  padding: 8px 10px;
  border-radius: 9px;
  font-size: 0.7rem;
  line-height: 1.4;
}

.success-note {
  color: #83e0a6;
  background: rgba(0, 168, 79, 0.085);
}

.submit-blocker {
  margin: 0 0 10px;
  font-size: 0.74rem;
}

.submit-btn {
  min-height: 48px;
  font-weight: 700;
  letter-spacing: 0;
}

.clear-btn {
  min-height: 40px;
  margin-top: 4px;
}

.clear-confirm {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 44px;
  margin-top: 8px;
  padding: 5px 6px 5px 12px;
  border: 1px solid rgba(244, 67, 54, 0.25);
  border-radius: 10px;
  background: rgba(244, 67, 54, 0.06);
  color: rgba(255, 255, 255, 0.76);
  font-size: 0.74rem;
}

.cart-list-enter-active,
.cart-list-leave-active {
  transition: opacity 180ms ease, transform 180ms ease, max-height 220ms ease;
}

.cart-list-enter,
.cart-list-leave-to {
  max-height: 0;
  opacity: 0;
  transform: translateX(12px);
}

.cart-drawer ::v-deep .v-btn:focus-visible,
.cart-drawer input:focus-visible {
  outline: 3px solid rgba(104, 220, 149, 0.78);
  outline-offset: 2px;
}

@media (max-width: 599px) {
  .cart-header {
    min-height: 82px;
    padding: 14px 16px;
  }

  .cart-items,
  .cart-form,
  .cart-footer {
    padding-right: 14px;
    padding-left: 14px;
  }

  .item-thumb {
    width: 60px;
    height: 60px;
    flex-basis: 60px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .cart-item,
  .cart-list-enter-active,
  .cart-list-leave-active {
    transition: none !important;
  }
}
</style>
